import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError, createBankAccount, updateBankAccount } from "@/lib/api";
import {
  bankAccountCreateSchema,
  type BankAccount,
  type BankAccountCreate,
  type BankAccountType,
} from "@finance/shared";

interface AccountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: BankAccount | null;
  onSaved: () => void;
}

interface FormState {
  name: string;
  type: BankAccountType;
  institution: string;
  current_balance: string;
  interest_rate: string;
  cd_maturity_date: string;
}

const EMPTY: FormState = {
  name: "",
  type: "checking",
  institution: "",
  current_balance: "",
  interest_rate: "",
  cd_maturity_date: "",
};

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  return new Date(ts).toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | null {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(ts)) return null;
  return new Date(ts).toISOString();
}

function fromAccount(account: BankAccount | null): FormState {
  if (!account) return EMPTY;
  return {
    name: account.name,
    type: account.type,
    institution: account.institution,
    current_balance: String(account.current_balance),
    interest_rate:
      account.interest_rate == null ? "" : String(account.interest_rate),
    cd_maturity_date: isoToDateInput(account.cd_maturity_date),
  };
}

export function AccountFormDialog({
  open,
  onOpenChange,
  account,
  onSaved,
}: AccountFormDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(fromAccount(account));
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [open, account]);

  const isEdit = account !== null;
  const showInterest = form.type === "savings" || form.type === "cd";
  const showMaturity = form.type === "cd";

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    const balance = Number(form.current_balance);
    if (form.current_balance === "" || Number.isNaN(balance)) {
      setErrors({ current_balance: "Enter a valid number" });
      return;
    }

    const interest =
      showInterest && form.interest_rate !== ""
        ? Number(form.interest_rate)
        : null;
    if (interest !== null && Number.isNaN(interest)) {
      setErrors({ interest_rate: "Enter a valid number" });
      return;
    }

    const payload: BankAccountCreate = {
      name: form.name.trim(),
      type: form.type,
      institution: form.institution.trim(),
      current_balance: balance,
      interest_rate: showInterest ? interest : null,
      cd_maturity_date: showMaturity
        ? dateInputToIso(form.cd_maturity_date)
        : null,
    };

    const parsed = bankAccountCreateSchema.safeParse(payload);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!flat[key]) flat[key] = issue.message;
      }
      setErrors(flat);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      if (isEdit && account) {
        await updateBankAccount(account.id, parsed.data);
      } else {
        await createBankAccount(parsed.data);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Failed to save account");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit account" : "Add account"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this manually tracked bank account."
              : "Track a checking, savings, or CD account."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="account-name">Name</Label>
            <Input
              id="account-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Everyday checking"
              autoFocus
              required
            />
            {errors.name ? (
              <p className="text-xs text-destructive">{errors.name}</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="account-type">Type</Label>
            <Select
              value={form.type}
              onValueChange={(value: BankAccountType) => update("type", value)}
            >
              <SelectTrigger id="account-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="checking">Checking</SelectItem>
                <SelectItem value="savings">Savings</SelectItem>
                <SelectItem value="cd">CD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="account-institution">Institution</Label>
            <Input
              id="account-institution"
              value={form.institution}
              onChange={(e) => update("institution", e.target.value)}
              placeholder="Chase, Fidelity, ..."
              required
            />
            {errors.institution ? (
              <p className="text-xs text-destructive">{errors.institution}</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="account-balance">Current balance</Label>
            <Input
              id="account-balance"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.current_balance}
              onChange={(e) => update("current_balance", e.target.value)}
              placeholder="0.00"
              required
            />
            {errors.current_balance ? (
              <p className="text-xs text-destructive">
                {errors.current_balance}
              </p>
            ) : null}
          </div>

          {showInterest ? (
            <div className="grid gap-1.5">
              <Label htmlFor="account-interest">Interest rate (%)</Label>
              <Input
                id="account-interest"
                type="number"
                inputMode="decimal"
                step="0.01"
                value={form.interest_rate}
                onChange={(e) => update("interest_rate", e.target.value)}
                placeholder="4.5"
              />
              {errors.interest_rate ? (
                <p className="text-xs text-destructive">
                  {errors.interest_rate}
                </p>
              ) : null}
            </div>
          ) : null}

          {showMaturity ? (
            <div className="grid gap-1.5">
              <Label htmlFor="account-maturity">CD maturity date</Label>
              <Input
                id="account-maturity"
                type="date"
                value={form.cd_maturity_date}
                onChange={(e) => update("cd_maturity_date", e.target.value)}
              />
            </div>
          ) : null}

          {submitError ? (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              role="alert"
            >
              {submitError}
            </p>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Add account"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
